import { InfoCircleOutlined } from '@ant-design/icons'
import { Edit } from '@mui/icons-material'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import styled from 'styled-components'
import { Popover } from 'sutra/components/Popover/Popover'
import { accents } from 'sutra/tokens/colors'
import { colorToHex } from 'sutra/utils/color'

// This file's colors are wrapped in colorToHex() rather than used as the raw
// oklch() tokens elsewhere in charts-v2: jsdom's CSS parser drops an entire
// declaration it can't parse (oklch() included), so a styled-components
// rule built from the raw token is silently unobservable via
// getComputedStyle in this directory's tests (ChartTypePicker.test.tsx,
// ParentMenuList.test.tsx), which assert on exact border colors. hex is
// visually identical to the source oklch token — see
// sutra/tokens/css-parity.test.ts for the exact-equivalence proof.
const REFLECTION_BLUE_50 = colorToHex(accents.reflectionBlue[50])
const REFLECTION_BLUE_100 = colorToHex(accents.reflectionBlue[100])
const REFLECTION_BLUE_500 = colorToHex(accents.reflectionBlue[500])

// The picker panels (`ParentMenu`, `ChildMenu`) draw their own card — background,
// radius, shadow — so the popover's own box is stripped to a bare positioning
// shell around them.
export const PickerPopover = styled(Popover)`
  padding: 0;
  background: transparent;
  box-shadow: none;
`

export const DropdownWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0;
  position: relative;
  /* Shrink to ParentMenu's width rather than filling the popup — ChildMenu
     is positioned absolutely and its \`left: 100%\` must resolve against
     ParentMenu's edge, not against whatever width an ancestor stretches to. */
  width: fit-content;
`

export const ParentMenu = styled.div`
  display: flex;
  flex-direction: column;
  width: 248px;
  padding: 8px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  /* Reserve room above/below the trigger so the popover's flip has somewhere
     to land — a looser cap lets the menu shift onto its own trigger. */
  max-height: min(480px, calc(100vh - 160px));
  overflow-y: auto;
`

export const ParentMenuItem = styled.div<{
  $isActive?: boolean
  $isSelected?: boolean
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  font-size: 14px;
  font-weight: 400;
  color: #374151;
  cursor: pointer;
  border-radius: 6px;
  border: 1px solid transparent;
  background-color: ${({ $isActive, $isSelected }) => {
    if ($isSelected) return REFLECTION_BLUE_50
    if ($isActive) return REFLECTION_BLUE_100
    return 'transparent'
  }};
  border-color: ${({ $isSelected }) =>
    $isSelected ? REFLECTION_BLUE_500 : 'transparent'};
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background-color: ${({ $isSelected }) =>
      $isSelected ? REFLECTION_BLUE_50 : REFLECTION_BLUE_100};
  }

  & > span:nth-child(2) {
    flex: 1;
  }
`

export const ChildMenu = styled.div<{
  $offsetTop?: number
  /** Opens to the LEFT of the parent menu instead of the right, when
   *  use-chart-type-picker.ts's useLayoutEffect measures that opening right
   *  would run past the viewport's right edge. Being `position: absolute`,
   *  this element contributes nothing to the popup's own bounding rect, so
   *  the popover's own viewport-collision flip (which only sees the parent
   *  menu) can never do this for us — see that hook for the measurement. */
  $flip?: boolean
}>`
  display: flex;
  flex-direction: column;
  width: 360px;
  padding: 8px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  position: absolute;
  top: ${({ $offsetTop }) => $offsetTop || 0}px;
  ${({ $flip }) =>
    $flip
      ? `
        right: 100%;
        margin-right: 8px;
      `
      : `
        left: 100%;
        margin-left: 8px;
      `}
  max-height: 500px;
  overflow-y: scroll;

  /* Always show scrollbar */
  &::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 6px;
    min-height: 40px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a1a1a1;
  }

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f1f1f1;
  scrollbar-gutter: stable;
`

export const MenuItemContainer = styled.div<{
  $disabled?: boolean
  $isSelected?: boolean
}>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  border-radius: 6px;
  border: 1px solid transparent;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  background-color: ${({ $disabled, $isSelected }) => {
    if ($disabled) return 'transparent'
    if ($isSelected) return REFLECTION_BLUE_50
    return 'transparent'
  }};
  border-color: ${({ $isSelected }) =>
    $isSelected ? REFLECTION_BLUE_500 : 'transparent'};

  &:hover {
    background-color: ${({ $disabled, $isSelected }) => {
      if ($disabled) return 'transparent'
      if ($isSelected) return REFLECTION_BLUE_50
      return REFLECTION_BLUE_100
    }};
  }

  & > div {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
`

export const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  flex-shrink: 0;

  & svg {
    width: 100%;
    height: auto;
  }
`

export const MenuItemLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #111827;
  line-height: 1.4;
`

export const MenuItemDescription = styled.div`
  font-size: 13px;
  font-weight: 400;
  color: #6b7280;
  line-height: 1.4;
`

export const Divider = styled.div`
  height: 1px;
  background-color: #e5e7eb;
  margin: 8px 0;
`

export const TemplatesHeading = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  font-weight: 500;
  margin: 2px 0 6px 0;
  padding-left: 4px;
  padding-right: 15px;
`

export const SectionHeading = styled.span`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 6px;
  padding-left: 4px;
`

export const TooltipDescription = styled.div`
  margin-top: 4px;
  color: gray;
`

export const IconSlot = styled.span`
  display: flex;
`

export const ChevronIcon = styled(KeyboardArrowRightIcon)`
  font-size: 16px;
`

export const InfoIcon = styled(InfoCircleOutlined)`
  font-size: 12px;
  color: #999;
`

export const FreeformIcon = styled(Edit)`
  font-size: 16px;
`

export const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

export const ChicletsRow = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  flex-shrink: 0;
`
